import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaPrint, FaArrowLeft } from "react-icons/fa";

const API = "https://nissi-india.com/api/boqReportPrint";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const Badge = ({ children, tone = "gray" }) => {
  const cls =
    tone === "green"
      ? "bg-green-100 text-green-800 border-green-200"
      : tone === "amber"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}
    >
      {children}
    </span>
  );
};

const EstimationDetailsPrint_1 = () => {
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key) => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(key) || "";
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ parsed values
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const preEstimationId = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return Number(qs.get("preEstimationId") || 0);
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // { companyName, header, items, totals }

  const load = async () => {
    if (!preEstimationId) {
      setError("preEstimationId missing in URL.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API}/printData`, {
        params: { preEstimationId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load print data");
        setData(null);
      } else {
        setData(res.data?.data || null);
      }
    } catch (e) {
      console.error(e);
      setError("Server error while loading print data");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preEstimationId]);

  const header = data?.header || {};
  const items = Array.isArray(data?.items) ? data.items : [];
  const totals = data?.totals || { qty: 0, amount: 0, amountWords: "ZERO" };
  const companyName = String(data?.companyName || "Company");

  const handlePrint = () => window.print();
  const handleBack = () => navigate(-1);

  const statusTone =
    String(header?.status || "").toLowerCase() === "approved"
      ? "green"
      : "amber";

  return (
    <div className="min-h-screen bg-gray-100">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .paper { box-shadow: none !important; border: none !important; }
          .table-wrap { border: 1px solid #e5e7eb !important; }
          .thead { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 font-semibold"
          >
            <FaArrowLeft /> Back
          </button>

          <div className="text-sm text-gray-600 font-medium">
            PreEstimation ID:{" "}
            <span className="text-gray-900 font-bold">
              {preEstimationId || "-"}
            </span>
          </div>

          <button
            onClick={handlePrint}
            disabled={loading || !!error}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-white ${
              loading || error
                ? "bg-emerald-400 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            <FaPrint /> Print
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="paper bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-8 border-b border-gray-200">
            <div className="flex items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center font-bold text-gray-500 text-xs">
                  LOGO
                </div>

                <div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {companyName}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    CIVIL CONSTRUCTION
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Address / Phone / Email
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="text-3xl font-black text-gray-900">
                  BOQ Report
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Generated: {new Date().toLocaleString("en-GB")}
                </div>
                <div className="mt-2">
                  <Badge tone={statusTone}>{header?.status || "Waiting"}</Badge>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">
                    Estimation Code
                  </div>
                  <div className="font-bold text-gray-900">
                    {header?.estimationCode || "-"}
                  </div>
                </div>
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">
                    Project Name
                  </div>
                  <div className="font-bold text-gray-900">
                    {header?.projectName || "-"}
                  </div>
                </div>
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">Remark</div>
                  <div className="font-bold text-gray-900">
                    {header?.remarks || "-"}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">
                    Customer Name
                  </div>
                  <div className="font-bold text-gray-900">
                    {header?.customerName || "-"}
                  </div>
                </div>
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">
                    Customer Address
                  </div>
                  <div className="font-bold text-gray-900">
                    {header?.customerAddress || "-"}
                  </div>
                </div>
                <div className="flex gap-3 py-1">
                  <div className="w-40 font-semibold text-gray-700">
                    PreEstimation ID
                  </div>
                  <div className="font-bold text-gray-900">
                    {preEstimationId || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="py-12 text-center">
                <div className="text-lg font-bold text-gray-900">
                  Loading BOQ…
                </div>
                <div className="text-gray-600 mt-1">Please wait</div>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <div className="text-lg font-bold text-red-600">{error}</div>
                <button
                  onClick={load}
                  className="mt-4 px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="table-wrap rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="thead bg-emerald-600 text-white">
                        <tr>
                          <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                            S.No
                          </th>
                          <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                            Work Type
                          </th>
                          <th className="px-3 py-3 text-left font-semibold border-r border-white/20">
                            Work Sub Type
                          </th>
                          <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                            UOM
                          </th>
                          <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                            Qty
                          </th>
                          <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                            Rate
                          </th>
                          <th className="px-3 py-3 text-right font-semibold border-r border-white/20">
                            Amount
                          </th>
                          <th className="px-3 py-3 text-left font-semibold">
                            Amt Words
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {items.length ? (
                          items.map((r) => (
                            <tr
                              key={`${r.sno}-${r.type}-${r.workSubTypeName}`}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-3 py-3 text-center align-top border-r border-gray-200">
                                {r.sno}
                                <div className="text-[10px] mt-1 text-gray-500">
                                  {r.type === "W"
                                    ? "Work"
                                    : r.type === "M"
                                    ? "Material"
                                    : ""}
                                </div>
                              </td>

                              <td className="px-3 py-3 align-top border-r border-gray-200 text-gray-900">
                                {r.workTypeName}
                              </td>

                              <td className="px-3 py-3 align-top border-r border-gray-200 text-gray-900">
                                <div className="whitespace-pre-wrap leading-relaxed">
                                  {r.workSubTypeName}
                                </div>
                              </td>

                              <td className="px-3 py-3 text-center align-top border-r border-gray-200 text-gray-900">
                                {r.uom}
                              </td>

                              <td className="px-3 py-3 text-right align-top border-r border-gray-200 text-gray-900">
                                {fmt2(r.qty)}
                              </td>

                              <td className="px-3 py-3 text-right align-top border-r border-gray-200 text-gray-900">
                                {fmt2(r.rate)}
                              </td>

                              <td className="px-3 py-3 text-right align-top border-r border-gray-200 text-gray-900">
                                {fmt2(r.amount)}
                              </td>

                              <td className="px-3 py-3 align-top text-gray-900">
                                <div className="whitespace-pre-wrap text-xs leading-relaxed">
                                  {String(r.amountWords || "").trim() || "-"}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center py-10 text-gray-600 font-semibold"
                            >
                              No items found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between md:block">
                      <div className="text-sm font-semibold text-gray-700">
                        Total Qty
                      </div>
                      <div className="text-xl font-black text-gray-900">
                        {fmt2(totals.qty)}
                      </div>
                    </div>

                    <div className="flex items-center justify-between md:block">
                      <div className="text-sm font-semibold text-gray-700">
                        Total Amount
                      </div>
                      <div className="text-xl font-black text-gray-900">
                        {fmt2(totals.amount)}
                      </div>
                    </div>

                    <div className="md:col-span-3">
                      <div className="text-sm font-semibold text-gray-700">
                        Amount in Words
                      </div>
                      <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 font-bold text-gray-900">
                        {String(totals.amountWords || "").trim() || "ZERO"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-gray-700">
                  <div className="text-center">
                    <div className="h-10 border-b border-dashed border-gray-300"></div>
                    <div className="mt-2 font-semibold">Prepared By</div>
                  </div>
                  <div className="text-center">
                    <div className="h-10 border-b border-dashed border-gray-300"></div>
                    <div className="mt-2 font-semibold">Checked By</div>
                  </div>
                  <div className="text-center">
                    <div className="h-10 border-b border-dashed border-gray-300"></div>
                    <div className="mt-2 font-semibold">Approved By</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-gray-500 mt-4">
          Generated by Infraconsoft
        </div>
      </div>
    </div>
  );
};

export default EstimationDetailsPrint_1;